import { LitElement, html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { t } from '../../i18n';

export interface TagLike { id: string; name: string; color?: string; }

/**
 * <cadence-tag-picker> — standard tag input: shows the assigned tags as removable chips,
 * with a search field that filters existing tags and offers "Create <name>" for new ones.
 * Presentational + decoupled from any domain: it emits intents and the host does the API
 * work.
 *   - `select`  detail: TagLike      → host assigns an existing tag
 *   - `create`  detail: string       → host creates a tag by name, then assigns it
 *   - `remove`  detail: string (id)  → host unassigns a tag
 */
@customElement('cadence-tag-picker')
export class CadenceTagPicker extends LitElement {
  @property({ attribute: false }) selected: TagLike[] = [];
  @property({ attribute: false }) options: TagLike[] = [];
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) allowCreate = true;
  @property({ type: String }) placeholder = '';

  @state() private q = '';
  @state() private open = false;
  @state() private active = 0;
  @query('input') private input!: HTMLInputElement;

  static styles = css`
    :host { display: block; }
    .box { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; padding: 6px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); }
    .box.disabled { opacity: 0.6; pointer-events: none; }
    .chip { display: inline-flex; align-items: center; gap: 5px; padding: 3px 8px; border-radius: 999px; background: var(--color-bg-secondary); font-size: 0.8rem; border: 1px solid var(--color-border); }
    .dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
    .chip .x { cursor: pointer; opacity: 0.6; font-weight: 700; line-height: 1; }
    .chip .x:hover { opacity: 1; }
    .wrap { position: relative; flex: 1; min-width: 120px; }
    input { width: 100%; box-sizing: border-box; border: none; outline: none; background: none; color: var(--color-text-primary); font: inherit; font-size: 0.85rem; padding: 4px 2px; }
    .menu { position: absolute; z-index: 30; left: 0; right: 0; top: calc(100% + 4px); background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: var(--radius-md); box-shadow: var(--shadow-md, 0 4px 16px rgba(0,0,0,0.12)); max-height: 220px; overflow-y: auto; padding: 4px; }
    .opt { display: flex; align-items: center; gap: 8px; padding: 6px 8px; border-radius: var(--radius-sm, 6px); cursor: pointer; font-size: 0.85rem; }
    .opt.active { background: var(--color-bg-secondary); }
    .opt .create { color: var(--color-text-secondary); }
    .opt .create b { color: var(--color-primary); font-weight: 600; }
    .empty { padding: 6px 8px; font-size: 0.82rem; color: var(--color-text-secondary); }
  `;

  private get suggestions(): TagLike[] {
    const q = this.q.trim().toLowerCase();
    const taken = new Set(this.selected.map((t) => t.id));
    return this.options
      .filter((t) => !taken.has(t.id) && (!q || t.name.toLowerCase().includes(q)))
      .slice(0, 50);
  }

  private get exactExists(): boolean {
    const q = this.q.trim().toLowerCase();
    return !!q && this.options.some((t) => t.name.toLowerCase() === q);
  }

  /** Whether to offer a "Create <q>" row. */
  private get canCreate(): boolean {
    return this.allowCreate && !!this.q.trim() && !this.exactExists;
  }

  /** Total selectable rows (suggestions + optional create row), for keyboard nav. */
  private get rows(): Array<{ type: 'tag'; tag: TagLike } | { type: 'create'; name: string }> {
    const r: Array<{ type: 'tag'; tag: TagLike } | { type: 'create'; name: string }> = this.suggestions.map((tag) => ({ type: 'tag' as const, tag }));
    if (this.canCreate) r.push({ type: 'create', name: this.q.trim() });
    return r;
  }

  private onInput(e: Event) {
    this.q = (e.target as HTMLInputElement).value;
    this.open = true;
    this.active = 0;
  }

  private onKeydown(e: KeyboardEvent) {
    const rows = this.rows;
    if (e.key === 'ArrowDown') { e.preventDefault(); this.open = true; this.active = Math.min(this.active + 1, rows.length - 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.active = Math.max(this.active - 1, 0); }
    else if (e.key === 'Enter') { e.preventDefault(); this.choose(rows[this.active]); }
    else if (e.key === 'Escape') { this.open = false; }
    else if (e.key === 'Backspace' && !this.q && this.selected.length) {
      this.dispatchEvent(new CustomEvent('remove', { detail: this.selected[this.selected.length - 1].id }));
    }
  }

  private choose(row: { type: 'tag'; tag: TagLike } | { type: 'create'; name: string } | undefined) {
    if (!row) return;
    if (row.type === 'tag') this.dispatchEvent(new CustomEvent('select', { detail: row.tag }));
    else this.dispatchEvent(new CustomEvent('create', { detail: row.name }));
    this.q = '';
    this.active = 0;
    this.open = false;
    // keep focus for rapid multi-tagging
    this.updateComplete.then(() => this.input?.focus());
  }

  render() {
    const rows = this.rows;
    return html`
      <div class="box ${this.disabled ? 'disabled' : ''}" @click=${() => this.input?.focus()}>
        ${this.selected.map((tag) => html`
          <span class="chip">
            <span class="dot" style="background:${tag.color || 'var(--color-primary)'}"></span>${tag.name}
            <span class="x" title=${t('ui.tagPicker.remove')} @click=${(e: Event) => { e.stopPropagation(); this.dispatchEvent(new CustomEvent('remove', { detail: tag.id })); }}>×</span>
          </span>`)}
        <div class="wrap">
          <input
            .value=${this.q}
            placeholder=${this.selected.length ? '' : (this.placeholder || t('ui.tagPicker.addTag'))}
            ?disabled=${this.disabled}
            @input=${this.onInput}
            @keydown=${this.onKeydown}
            @focus=${() => (this.open = true)}
            @blur=${() => setTimeout(() => (this.open = false), 150)} />
          ${this.open && (rows.length || this.q.trim()) ? html`
            <div class="menu">
              ${rows.length ? rows.map((row, i) => html`
                <div class="opt ${i === this.active ? 'active' : ''}"
                  @mousedown=${(e: Event) => { e.preventDefault(); this.choose(row); }}
                  @mouseenter=${() => (this.active = i)}>
                  ${row.type === 'tag'
                    ? html`<span class="dot" style="background:${row.tag.color || 'var(--color-primary)'}"></span>${row.tag.name}`
                    : html`<span class="create">${t('ui.tagPicker.create')} <b>${row.name}</b></span>`}
                </div>`)
                : html`<div class="empty">${t('ui.tagPicker.noMatches')}</div>`}
            </div>` : ''}
        </div>
      </div>`;
  }
}
